import { useState, useEffect, useMemo } from 'react';
import { rooms as roomsApi, bookings as bookingsApi, pricingPeriods as pricingPeriodsApi } from '../utils/api';
import api from '../utils/api';
import { ArrowRight, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function minTimeStr() {
  const d = new Date(Date.now() - 20*60*1000);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// Recalcule le prix d'un profil pour N joueurs selon HC/HP
function calcTierPrice(profile, playerCount, appliedType) {
  const tiers = (profile.tiers || [])
    .filter(t => playerCount >= (t.minPlayers || t.min_players || 1))
    .sort((a, b) => (b.minPlayers||b.min_players||0) - (a.minPlayers||a.min_players||0));
  if (!tiers.length) return null;
  const tier = tiers[0];
  const type = appliedType || 'hc';
  const priceFlat = type === 'hp'
    ? (tier.priceFlatHp || tier.priceFlatHc || tier.price_flat_hp || tier.price_flat_hc)
    : (tier.priceFlatHc || tier.priceFlatHp || tier.price_flat_hc || tier.price_flat_hp);
  const pricePerP = type === 'hp'
    ? (tier.pricePerPersonHp || tier.pricePerPersonHc || tier.price_per_person_hp || tier.price_per_person_hc)
    : (tier.pricePerPersonHc || tier.pricePerPersonHp || tier.price_per_person_hc || tier.price_per_person_hp);
  return priceFlat
    ? parseFloat(priceFlat)
    : parseFloat(pricePerP || 0) * playerCount;
}

export default function MoveBookingModal({ booking, onClose, onMoved }) {
  const [rooms,       setRooms]   = useState([]);
  const [targetRoom,  setTarget]  = useState(booking.room_id);

  const parseBookingDate = (d) => {
    if (!d) return localDateStr();
    const s = typeof d === 'string' ? d : d.toISOString();
    return localDateStr(new Date(s));
  };

  const [newDate,     setDate]       = useState(parseBookingDate(booking.session_date));
  const [newSlot,     setSlot]       = useState(booking.session_start?.slice(0,5));
  const [appliedType, setApplied]    = useState(null);
  const [takenSlots,  setTaken]      = useState([]);
  const [roomPricing, setPricing]    = useState(null);
  const [saving,      setSaving]     = useState(false);
  const [avoirModal,  setAvoir]      = useState(null);

  const minDate     = localDateStr();
  const minTime     = minTimeStr();
  const playerCount = booking.player_count || 1;

  // Breakdown original de la réservation
  const origBreakdown = useMemo(() => {
    const pb = booking.price_breakdown;
    if (!pb) return [];
    if (Array.isArray(pb)) return pb;
    try { return JSON.parse(pb); }
    catch { return []; }
  }, [booking.price_breakdown]);

  // Charger les salles
  useEffect(() => {
    roomsApi.list().then(r => setRooms((r.data.rooms||[]).filter(rm => rm.is_active && !rm.is_addon)));
  }, []);

  // Charger les résas du jour
  useEffect(() => {
    if (!targetRoom || !newDate) return;
    setSlot('');
    setTaken([]);
    bookingsApi.public({ roomId: targetRoom, date: newDate })
      .then(r => setTaken(r.data.bookings || []))
      .catch(() => {});
  }, [targetRoom, newDate]);

  // Résoudre HC/HP
  useEffect(() => {
    if (!targetRoom || !newDate || !newSlot) { setApplied(null); return; }
    pricingPeriodsApi.resolve({ roomId: targetRoom, date: newDate, time: newSlot })
      .then(r => setApplied(r.data.appliedType || null))
      .catch(() => setApplied(null));
  }, [targetRoom, newDate, newSlot]);

  // Charger les tarifs de la salle cible
  useEffect(() => {
    if (!targetRoom) return;
    setPricing(null);
    api.get(`/rooms/${targetRoom}/pricing`)
      .then(r => setPricing(r.data.pricing || []))
      .catch(() => {});
  }, [targetRoom]);

  // Créneaux disponibles
  const availableSlots = useMemo(() => {
    const room = rooms.find(rm => rm.id === targetRoom);
    if (!room) return [];
    const [y,m,d] = newDate.split('-').map(Number);
    const dow = new Date(y, m-1, d).getDay();
    return (room.time_slots || [])
      .filter(s => {
        const days = Array.isArray(s.day_of_week) ? s.day_of_week : [];
        if (days.length && !days.includes(dow)) return false;
        return true;
      })
      .map(s => s.start_time.slice(0,5))
      .sort()
      .filter(s => {
        if (takenSlots.some(b => b.session_start?.slice(0,5) === s && b.id !== booking.id)) return false;
        if (newDate === minDate && s < minTime) return false;
        return true;
      });
  }, [rooms, targetRoom, newDate, takenSlots, minDate, minTime, booking.id]);

  // Résumé de prix — recalcule en conservant la composition originale
  const priceSummary = useMemo(() => {
    const slotChanged = newSlot && newSlot !== booking.session_start?.slice(0,5);
    const roomChanged  = targetRoom !== booking.room_id;
    if (!newSlot || (!roomChanged && !slotChanged)) return null;
    if (!roomPricing || !appliedType) return null;

    const oldTotal = parseFloat(booking.total_amount || 0);
    console.log('[Move] origBreakdown:', origBreakdown, 'roomPricing:', roomPricing?.map(p=>p.profile_id+'/'+p.profile_name));
    let newTotal = 0;
    const newBreakdown = [];

    // Si on a un breakdown original, recalculer chaque profil
    if (origBreakdown.length > 0) {
      for (const orig of origBreakdown) {
        if (!orig.qty || orig.qty === 0) continue;
        // Trouver le profil correspondant dans roomPricing
        const profile = roomPricing.find(p =>
          p.profile_id === orig.profileId ||
          p.profile_name?.toLowerCase() === orig.profileName?.toLowerCase()
        );
        if (profile) {
          const lineTotal = calcTierPrice(profile, orig.qty, appliedType);
          if (lineTotal !== null) {
            newTotal += lineTotal;
            newBreakdown.push({
              profileId:   profile.profile_id,
              profileName: profile.profile_name,
              qty:         orig.qty,
              lineTotal,
            });
          }
        }
      }
    }

    // Si pas de breakdown ou profils non trouvés, fallback sur profil principal
    if (newBreakdown.length === 0) {
      const main = roomPricing.find(p => p.profile_name?.toLowerCase().includes('plein')) || roomPricing[0];
      if (!main) return null;
      const lt = calcTierPrice(main, playerCount, appliedType);
      if (lt === null) return null;
      newTotal = lt;
      newBreakdown.push({ profileId: main.profile_id, profileName: main.profile_name, qty: playerCount, lineTotal: lt });
    }

    const diff = parseFloat((newTotal - oldTotal).toFixed(2));
    return {
      newTotal,
      oldTotal,
      diff,
      breakdown: newBreakdown,
      appliedType,
      equivalent: Math.abs(diff) < 0.01,
    };
  }, [roomPricing, appliedType, origBreakdown, targetRoom, booking, newSlot, playerCount]);

  const roomChanged = targetRoom !== booking.room_id;
  const dateChanged = newDate !== booking.session_date?.slice(0,10);
  const slotChanged = newSlot && newSlot !== booking.session_start?.slice(0,5);
  const hasChanged  = (roomChanged || dateChanged || slotChanged) && !!newSlot;

  const doSave = async () => {
    if (!newSlot) return toast.error('Choisissez un créneau');
    if (newDate < minDate) return toast.error('La date ne peut pas être dans le passé');
    if (newDate === minDate && newSlot < minTime) return toast.error('Ce créneau est déjà passé');
    setSaving(true);
    try {
      const payload = { roomId: targetRoom, sessionDate: newDate, sessionStart: newSlot };
      if (priceSummary) {
        payload.totalAmount    = priceSummary.newTotal;
        payload.priceBreakdown = JSON.stringify(priceSummary.breakdown);
      }
      await bookingsApi.update(booking.id, payload);
      if (priceSummary && priceSummary.diff < -0.01) {
        setAvoir({ amount: Math.abs(priceSummary.diff), bookingId: booking.id });
      } else {
        toast.success('Réservation déplacée');
        onMoved();
      }
    } catch(e) {
      toast.error(e.response?.data?.error || 'Erreur lors du déplacement');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <h3 className="modal-title">Déplacer la réservation</h3>

        {/* Récap actuel */}
        <div style={{ marginBottom:20, padding:'10px 14px', background:'var(--bg-elevated)',
          borderRadius:8, fontSize:13, color:'var(--text-2)', display:'flex', gap:16, flexWrap:'wrap' }}>
          <span>Salle : <strong>{booking.room_name}</strong></span>
          <span>Date : <strong>{parseBookingDate(booking.session_date)}</strong></span>
          <span>Heure : <strong>{booking.session_start?.slice(0,5)}</strong></span>
          <span>Joueurs : <strong>{playerCount}</strong></span>
          <span>Total : <strong>{parseFloat(booking.total_amount||0).toFixed(2)} €</strong></span>
        </div>

        <div className="form-group">
          <label className="form-label">Nouvelle salle</label>
          <select className="input" value={targetRoom}
            onChange={e => { setTarget(e.target.value); setSlot(''); }}>
            {rooms.map(rm => (
              <option key={rm.id} value={rm.id}>
                {rm.name}{rm.id === booking.room_id ? ' (actuelle)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Nouvelle date</label>
            <input type="date" className="input" value={newDate} min={minDate}
              onChange={e => { setDate(e.target.value); setSlot(''); }}/>
          </div>
          <div className="form-group">
            <label className="form-label">
              Créneau
              {availableSlots.length > 0 && (
                <span style={{ color:'var(--text-3)', fontWeight:400, marginLeft:6 }}>
                  — {availableSlots.length} dispo
                </span>
              )}
            </label>
            {availableSlots.length === 0 ? (
              <div style={{ padding:'9px 12px', borderRadius:8,
                border:'1px solid var(--border)', background:'var(--bg-elevated)',
                fontSize:13, color:'var(--text-3)' }}>
                Aucun créneau disponible ce jour
              </div>
            ) : (
              <select className="input" value={newSlot} onChange={e => setSlot(e.target.value)}>
                <option value="">— Choisir —</option>
                {availableSlots.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* Résumé prix */}
        {priceSummary && (
          <div style={{ marginBottom:12, padding:'12px 14px', borderRadius:10, fontSize:13,
            background: priceSummary.equivalent ? 'rgba(0,210,180,0.08)'
              : priceSummary.diff > 0 ? 'rgba(245,166,35,0.08)' : 'rgba(0,210,180,0.08)',
            border:`1px solid ${priceSummary.equivalent ? 'var(--teal)'
              : priceSummary.diff > 0 ? 'var(--amber)' : 'var(--teal)'}` }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
              {priceSummary.equivalent
                ? <CheckCircle2 size={14} color="var(--teal)"/>
                : <Info size={14} color={priceSummary.diff > 0 ? 'var(--amber)' : 'var(--teal)'}/>}
              <strong style={{ color: priceSummary.equivalent ? 'var(--teal)'
                : priceSummary.diff > 0 ? 'var(--amber)' : 'var(--teal)' }}>
                {priceSummary.equivalent ? 'Tarif équivalent'
                  : priceSummary.diff > 0 ? 'Tarif supérieur' : 'Tarif inférieur'}
              </strong>
              {priceSummary.appliedType && (
                <span style={{ fontSize:11, padding:'1px 7px', borderRadius:99,
                  background: priceSummary.appliedType === 'hp' ? 'rgba(245,100,50,0.15)' : 'rgba(0,210,180,0.15)',
                  color: priceSummary.appliedType === 'hp' ? '#f56432' : 'var(--teal)' }}>
                  {priceSummary.appliedType.toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ display:'flex', gap:20, flexWrap:'wrap', color:'var(--text-2)' }}>
              <span>Ancien : {priceSummary.oldTotal.toFixed(2)} €</span>
              <span>Nouveau : <strong>{priceSummary.newTotal.toFixed(2)} €</strong></span>
              {!priceSummary.equivalent && (
                <span style={{ fontWeight:700,
                  color: priceSummary.diff > 0 ? 'var(--amber)' : 'var(--teal)' }}>
                  {priceSummary.diff > 0 ? '+' : ''}{priceSummary.diff.toFixed(2)} €
                </span>
              )}
            </div>
            {/* Détail composition */}
            {priceSummary.breakdown.length > 1 && (
              <div style={{ marginTop:8, display:'flex', gap:12, flexWrap:'wrap' }}>
                {priceSummary.breakdown.map((b, i) => (
                  <span key={i} style={{ fontSize:11, color:'var(--text-3)' }}>
                    {b.profileName} x{b.qty} = {b.lineTotal.toFixed(2)} €
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {hasChanged && !priceSummary && newSlot && roomPricing !== null && (
          <div style={{ marginBottom:12, padding:'10px 14px', borderRadius:8,
            background:'rgba(245,166,35,0.06)', border:'1px solid var(--amber)', fontSize:13 }}>
            <AlertCircle size={13} color="var(--amber)" style={{ marginRight:6, verticalAlign:'middle' }}/>
            <span style={{ color:'var(--amber)' }}>
              Tarif non déterminé pour ce créneau
            </span>
          </div>
        )}

        {/* Modale avoir */}
        {avoirModal && (
          <div style={{ position:'fixed', inset:0, zIndex:300,
            background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div className="modal" style={{ maxWidth:380, textAlign:'center' }}>
              <div style={{ fontSize:40, marginBottom:12 }}>💳</div>
              <h3 className="modal-title">Générer un avoir ?</h3>
              <p style={{ color:'var(--text-2)', fontSize:14, marginBottom:20 }}>
                Le nouveau tarif est inférieur de{' '}
                <strong style={{ color:'var(--teal)' }}>{avoirModal.amount.toFixed(2)} €</strong>.
                Voulez-vous créer un avoir pour ce client ?
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <button className="btn btn-teal" style={{ justifyContent:'center' }}
                  onClick={async () => {
                    try {
                      await api.post('/gift-cards', {
                        purchasedByName:  booking.customer_name,
                        purchasedByEmail: booking.customer_email,
                        initialAmount:    avoirModal.amount,
                        giftType:         'avoir',
                        sourceBookingId:  booking.id,
                        customerId:       booking.customer_id,
                        expiresInDays:    365,
                        notes: 'Avoir suite au déplacement de la réservation ' + booking.booking_ref,
                      });
                      toast.success('Avoir de ' + avoirModal.amount.toFixed(2) + ' € généré');
                    } catch {
                      toast.error('Erreur lors de la création du bon');
                    }
                    setAvoir(null);
                    onMoved();
                  }}>
                  Créer l'avoir de {avoirModal.amount.toFixed(2)} €
                </button>
                <button className="btn btn-secondary" style={{ justifyContent:'center' }}
                  onClick={() => { setAvoir(null); toast.success('Réservation déplacée'); onMoved(); }}>
                  Déplacer sans avoir
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={doSave} disabled={saving || !hasChanged}>
            {saving
              ? <span className="spinner" style={{width:14,height:14}}/>
              : <><ArrowRight size={14}/> Déplacer</>}
          </button>
        </div>
      </div>
    </div>
  );
}
